/**
 * Domain utilities — single source of truth for redirect URLs
 * 
 * ALLOWED_REDIRECT_PATHS: whitelist of internal paths that auth redirect params can target.
 * getAuthRedirectUrl: canonical redirect URL for Supabase auth (signup, OAuth, password reset).
 */

/** Internal paths that are valid redirect targets after auth */
const ALLOWED_REDIRECT_PREFIXES = [
  '/assistant',
  '/wallet',
  '/settings',
  '/exam',
  '/dashboard',
  '/subscription',
  '/promo',
  '/help',
  '/faq',
  '/notifications',
  '/parent',
  '/admin',
  '/value',
  '/talk',
  '/uram',
  '/trust',
  '/merchant',
  '/donate',
  '/fuel',
  '/location',
  '/places',
  '/guides',
  '/editorial',
  '/finance',
  '/business',
  '/referral',
  '/auth',
  '/provider',
  '/request',
  '/requests',
  '/auto',
  '/travel',
  '/',
];

/**
 * Validate a redirect path is internal and safe.
 * Returns the path if valid, or fallback otherwise.
 */
export function sanitizeRedirectPath(raw: string | null, fallback = '/'): string {
  if (!raw) return fallback;

  // Must start with / and not with // (prevent protocol-relative URLs)
  const hasControlCharacter = Array.from(raw).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127);
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\') || hasControlCharacter) return fallback;

  // Strip query/hash for prefix matching
  const pathOnly = raw.split('?')[0].split('#')[0];

  const isAllowed = ALLOWED_REDIRECT_PREFIXES.some(
    prefix => pathOnly === prefix || pathOnly.startsWith(prefix + '/')
  );

  return isAllowed ? raw : fallback;
}

export const getDomain = () => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost';
  const isLovable = hostname.includes('lovableproject.com') || hostname.includes('lovable.app');
  const isDevelopment = isLocalhost || isLovable;
  
  return {
    isTemplatesDomain: false,
    isMainDomain: hostname === 'lavlah.mn' || hostname === 'www.lavlah.mn' || isDevelopment,
    isLocalhost,
    isLovable,
    hostname
  };
};

export const getActiveHost = () => {
  return window.location.hostname;
};

export const isRootHost = () => {
  const hostname = getActiveHost();
  return hostname === 'lavlah.mn' || 
         hostname === 'www.lavlah.mn' ||
         hostname === 'localhost' ||
         hostname.includes('lovableproject.com') ||
         hostname.includes('lovable.app');
};

export const canonicalBase = () => {
  // Search metadata always identifies production; auth redirects remain host-aware.
  return 'https://lavlah.mn';
};

/**
 * Canonical redirect URL for Supabase auth operations.
 * Always returns origin without trailing slash — Supabase appends its own path fragments.
 */
export const getAuthRedirectUrl = (nextPath?: string | null) => {
  const { hostname, isLocalhost, isLovable } = getDomain();

  // www and apex must both resolve to the single canonical origin that is
  // registered in Supabase's redirect allow-list, otherwise OAuth callbacks
  // fail with "requested path is invalid".
  let base: string;
  if (hostname === 'lavlah.mn' || hostname === 'www.lavlah.mn') {
    base = 'https://lavlah.mn';
  } else if (isLocalhost || isLovable) {
    base = window.location.origin.replace(/\/$/, '');
  } else {
    base = `${window.location.protocol}//${hostname}`;
  }

  const safePath = sanitizeRedirectPath(nextPath ?? null, '/');
  return safePath === '/' ? base : `${base}${safePath}`;
};
